import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule, ScrollStrategy, createRepositionScrollStrategy } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    Type,
    ViewEncapsulation,
    inject,
    signal,
} from '@angular/core';
import { Column, Table } from '@tanstack/angular-table';
import { DropdownFilterValue } from './grid-filter.fns';

/**
 * The uniform input set every filter control declares — see `FtDataTableComponent.filterInputs` and
 * `SponsorCustomReportComponent.filterInputsFor`. Kept as one shape because `ComponentRef.setInput`
 * throws in dev for an input the mounted component does not declare.
 */
export interface FtFilterControlInputs {
    column: Column<any, unknown>;
    table: Table<any>;
    compareParam: string | null;
}

/**
 * AG Grid's column menu: a hamburger in the header cell that opens the column's filter in a popover.
 *
 * 🚨 THIS EXISTS BECAUSE THE CONTROLS CANNOT RENDER INLINE. Both engines the TanStack cutover
 * replaced put their filters behind a menu — AG Grid's column menu, PrimeNG's `<p-columnFilter>`
 * overlay — so the header stayed ONE ROW tall. Mounting them inline instead made the multi-select a
 * permanently-expanded option list: measured `<thead>` heights were 41px on both legacy engines
 * against 289px on the admin dashboard and **704px** on sponsor-custom-report's Campaigns Summary,
 * whose 36-option fieldset alone was 647px.
 *
 * Two attempts before this one, both measured on the live hosts and both rejected as still too tall:
 * capping the option list at 132px (177px / 189px), then collapsing each control to a full-width
 * "All ▾" trigger (73px / 85px). The trigger read better — it showed the active filter as text — but
 * it still cost a whole extra header row. An icon in the EXISTING header cell costs none, which is
 * why the legacy engines did it that way.
 *
 * Used by BOTH consumers of the controls, which is the point of factoring it out: the wrapper's own
 * header (`data-table.component.html`) and sponsor-custom-report's custom header template, which
 * replaces it and mounts the same controls in its own `<th>`.
 *
 * 🚨 The panel renders in the CDK overlay container, i.e. attached to `document.body` and OUTSIDE
 * `<ft-data-table>`. Two consequences, both load-bearing:
 *
 *   - Its CSS cannot live in `data-table.component.scss`. Even `:host ::ng-deep` there is anchored to
 *     the data-table host, which the panel is not inside. So this component owns the control styling
 *     and declares `ViewEncapsulation.None`, with every rule scoped under `.ft-data-table__filter-
 *     trigger` / `.ft-data-table__filter-panel` so nothing leaks.
 *   - The control component is DESTROYED when the panel closes and rebuilt on the next open, so any
 *     control holding internal state must be able to restore it from `column.getFilterValue()`.
 *     `FtDropdownFilterComponent` is the only one that does hold state; see the note on its
 *     `initialValues`.
 *
 * The alternative — `position: absolute` inside the `<th>` — was rejected: sponsor-custom-report
 * wraps the table in an `overflow: auto` container with a max-height, which clips it.
 */
@Component({
    selector: 'ft-filter-popover',
    standalone: true,
    imports: [CommonModule, OverlayModule, A11yModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ft-filter-popover.component.scss'],
    template: `
        <button type="button"
                class="ft-data-table__filter-trigger"
                [class.-active]="isActive()"
                cdkOverlayOrigin
                #origin="cdkOverlayOrigin"
                aria-haspopup="dialog"
                [attr.aria-expanded]="open()"
                [attr.aria-label]="panelLabel()"
                [attr.title]="panelLabel()"
                (click)="toggle()"
                (keydown.escape)="close()">
            <span class="ft-data-table__filter-trigger-icon" aria-hidden="true">≡</span>
        </button>

        <ng-template cdkConnectedOverlay
                     [cdkConnectedOverlayOrigin]="origin"
                     [cdkConnectedOverlayOpen]="open()"
                     [cdkConnectedOverlayPositions]="positions"
                     [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
                     [cdkConnectedOverlayPush]="true"
                     cdkConnectedOverlayPanelClass="ft-filter-overlay-pane"
                     [cdkConnectedOverlayHasBackdrop]="true"
                     cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                     (backdropClick)="close()"
                     (detach)="close()"
                     (overlayKeydown)="onPanelKeydown($event)">
            <div class="ft-data-table__filter-panel"
                 role="dialog"
                 [attr.aria-label]="panelLabel()"
                 cdkTrapFocus
                 [cdkTrapFocusAutoCapture]="true">
                <ng-container *ngComponentOutlet="control; inputs: inputs" />
            </div>
        </ng-template>
    `,
})
export class FtFilterPopoverComponent {
    /** The headless control to mount in the panel (FtTextFilterComponent, FtDropdownFilterComponent, …). */
    @Input() control!: Type<unknown>;
    @Input() inputs!: FtFilterControlInputs;
    /** Column label for the accessible name; falls back to the column id. */
    @Input() label = '';

    private readonly injector = inject(Injector);

    readonly open = signal(false);

    /**
     * Reposition rather than close, so the panel tracks its column while the page (or, on
     * sponsor-custom-report, the table's own `overflow: auto` container) scrolls; `autoClose` gives up
     * and closes once the trigger itself has scrolled out of view.
     *
     * ⚠ For a scrolling ANCESTOR this only fires if that container is registered with the
     * `ScrollDispatcher` — i.e. carries `cdkScrollable`. sponsor-custom-report's wrapper does.
     */
    readonly scrollStrategy: ScrollStrategy = createRepositionScrollStrategy(this.injector, { autoClose: true });

    /**
     * Below the icon by preference, above it when there is no room. `end`-aligned FIRST: the icon sits
     * at the right edge of its header cell, and a panel far wider than the icon has to hang back into
     * the table rather than off its right side.
     */
    readonly positions: ConnectedPosition[] = [
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    toggle(): void {
        this.open.set(!this.open());
    }

    close(): void {
        if (this.open()) {
            this.open.set(false);
        }
    }

    /**
     * 🚨 `stopPropagation` is not optional. sponsor-custom-report renders inside a `MatDialog`, and an
     * Escape that keeps bubbling closes the DIALOG — i.e. dismissing a filter would throw away the
     * whole report.
     */
    onPanelKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.close();
        }
    }

    /** Whether this column currently filters anything — the only cue left once the panel is closed. */
    isActive(): boolean {
        const value = this.inputs?.column?.getFilterValue();
        return value != null && value !== '';
    }

    /**
     * The current filter in words. Reads the column's filter VALUE rather than asking the control, so
     * one summary covers every filter type (the control is not even instantiated while closed).
     *
     * The icon has no room for this, so it rides on `title`/`aria-label` — see `panelLabel`. Losing the
     * at-a-glance summary is the price of the hamburger; `-active` on the icon is what keeps a filtered
     * column visible without hovering it.
     */
    summary(): string {
        const value = this.inputs?.column?.getFilterValue();
        if (value == null || value === '') {
            return 'All';
        }
        const selected = (value as DropdownFilterValue)?.selected;
        if (Array.isArray(selected)) {
            if (selected.length === 0) {
                return 'None';
            }
            return selected.length === 1 ? `${selected[0]}` : `${selected.length} selected`;
        }
        return `${value}`;
    }

    /** Accessible name for the icon and the panel; carries the summary the icon cannot show. */
    panelLabel(): string {
        const name = `${this.label || this.inputs?.column?.id || ''}`.trim();
        return this.isActive() ? `Filter ${name} — ${this.summary()}` : `Filter ${name}`;
    }
}
